import React, { useMemo, useEffect } from 'react'
import { useForm, FormProvider, useFieldArray, Controller, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Box, Grid, IconButton, Stack } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { Dialog } from '@e3dc-react/shell/Elements/Dialog'
import { Select } from '@e3dc-react/shell/Elements/Select'
import { ErrorMessage } from '@e3dc-react/shell/Elements/Error'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { useFetchTariffs } from '../../../tariffs/api/use-fetch-tariffs'
import { useAssignTariffs } from '../../api/use-assign-tariffs'
import { AssignTariffSkeleton } from './AssignTariffSkeleton'
import { TariffAccordionItem } from './TariffAccordionItem'
import { AssignNewTariffButton } from './AssignNewTariffButton'
import { TariffCategory, TariffStatusType } from 'src/Libs/e-mobility/shared/models/types'
import { createAssignTariffSchema, AssignTariffFormData } from './assign-tariff.schema'
import { WallboxModel } from 'src/Libs/e-mobility/wallboxes'
import { useAccordionStyles } from 'src/Libs/e-mobility/shared/styles/accordion.styles'

const statusOrderMapper: Record<TariffStatusType, number> = {
  active: 0,
  inactive: 0,
  expired: 1,
  'not-in-use': 1,
}

export interface AssignTariffDialogProps {
  wallbox: WallboxModel
  installationId: number
  open: boolean
  onClose: () => void
  place: number
}

const CONNECTOR_ID = 1

/**
 *  Represents dialog component for assigning tariffs to a snigle wallbox.
 *  @param wallbox - single WallboxModel
 *  @param installationId - unique installation identifier as string
 *  And other params from useModals hook
 */
export const AssignTariffDialog: React.FC<AssignTariffDialogProps> = ({ wallbox, installationId, onClose, open, place }) => {
  const { t } = useCustomTranslation()
  const { classes } = useAccordionStyles()

  const { tariffResponse, isError, isLoading, isSuccess } = useFetchTariffs(installationId)

  const { assignTariff, isLoading: isAssignTariffLoading } = useAssignTariffs(onClose, Number(installationId))

  const tariffInitialValues = useMemo(
    () => [
      ...(wallbox.activeTariff
        ? [
            {
              value: wallbox.activeTariff.tariffId,
              tariffStatus: wallbox.activeTariff.tariffStatus,
            },
          ]
        : []),
      ...wallbox.inactiveTariffs.map(tariff => ({
        value: tariff.tariffId,
        tariffStatus: tariff.tariffStatus,
      })),
      ...wallbox.expiredTariffs.map(tariff => ({
        value: tariff.tariffId,
        tariffStatus: tariff.tariffStatus,
      })),
    ],
    [wallbox]
  )

  const dropdownTariffs = useMemo(
    () =>
      tariffResponse.tariffs.map(tariff => ({
        value: tariff.tariffId,
        tariffName: tariff.tariffName,
        wallboxType: wallbox.wallboxType,
        gridPrice: tariff.staticPrices.gridPrice,
        dischargePrice: tariff.dischargePrice,
        solarPrice: tariff.staticPrices.solarPrice,
        startDate: tariff.startTime,
        endDate: tariff.endTime,
        tariffStatus: tariff.tariffStatus,
        currencyCode: tariff.currencyCode,
        tariffCategory: tariff.tariffCategory,
      })),
    [tariffResponse, wallbox.wallboxType]
  )

  const selectableTariffs = useMemo(
    () =>
      dropdownTariffs
        .filter(tariff => tariff.tariffCategory === TariffCategory.staticTariff)
        .sort((tariffA, tariffB) => {
          /** active and inactive tariffs on top, expired on bottom */
          const tariffStatusDiff = statusOrderMapper[tariffA.tariffStatus] - statusOrderMapper[tariffB.tariffStatus]

          if (tariffStatusDiff !== 0) {
            return tariffStatusDiff
          }

          /** then sort tariffs by their name */
          return tariffA.tariffName.localeCompare(tariffB.tariffName)
        }),
    [dropdownTariffs]
  )

  const tariffMap = useMemo(() => Object.fromEntries(tariffResponse.tariffs.map(tariff => [tariff.tariffId, tariff])), [tariffResponse.tariffs])

  const schema = useMemo(() => createAssignTariffSchema(t, tariffInitialValues, dropdownTariffs), [t, tariffInitialValues, dropdownTariffs])

  const form = useForm<AssignTariffFormData>({
    resolver: zodResolver(schema),
    defaultValues: { tariffs: tariffInitialValues },
    mode: 'onChange',
  })

  const {
    control,
    handleSubmit,
    formState: { isDirty, isValid },
    trigger,
  } = form

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'tariffs',
  })

  const watchedTariffs = useWatch({
    control,
    name: 'tariffs',
  })

  const onSubmit = (data: AssignTariffFormData): void => {
    assignTariff({
      tariffIds: data.tariffs.map(tariff => tariff.value),
      connectorId: CONNECTOR_ID,
      wallboxId: wallbox.wallboxId,
      installationId: Number(installationId),
    })
  }

  /**
   * Validate form immediately based on default values to check if the tariff that is already assigned became
   * expired, therefore requiring an error message to be shown next to the Tariff dropdown.
   */
  useEffect(() => {
    trigger()
  }, [trigger])

  return (
    <FormProvider {...form}>
      <Dialog
        maxWidth="md"
        title={t('emobility.wallbox.editTariffs.title', {
          wallbox: wallbox.wallboxName,
          id: wallbox.wallboxId,
        })}
        onClose={onClose}
        place={place}
        actions={{
          primary: {
            disabled: !isValid || !isDirty || isAssignTariffLoading,
            isLoading: isAssignTariffLoading,
            name: t('emobility.wallbox.editTariffs.save'),
            onClick: handleSubmit(onSubmit),
          },
          secondary: { name: t('emobility.wallbox.editTariffs.cancel') },
        }}
      >
        {isLoading && <AssignTariffSkeleton />}
        {isError && <ErrorMessage title={t('emobility.tariffs.error-message')} />}

        {isSuccess && (
          <Stack spacing={3} mt={2}>
            <Box>
              <AssignNewTariffButton onAddTariff={() => append({ value: '' })} />
            </Box>

            {fields.map((field, index) => {
              const selectedValue = watchedTariffs?.[index]?.value
              const selectedTariff = selectedValue ? tariffMap[selectedValue] : undefined

              return (
                <Stack key={field.id} className={classes.container}>
                  <Grid container spacing={3} p={3}>
                    <Grid item xs={11}>
                      <Controller
                        control={control}
                        name={`tariffs.${index}.value`}
                        render={({ field, fieldState }) => (
                          <Select
                            {...field}
                            label={t('emobility.wallbox.editTariffs.tariffSelectLabel')}
                            options={selectableTariffs.map(tariff => ({
                              value: tariff.value,
                              label:
                                tariff.tariffStatus === TariffStatusType.expired
                                  ? `${tariff.tariffName} (${t('emobility.tariffs.tariff-status-expired')})`
                                  : tariff.tariffName,
                            }))}
                            helperText={fieldState.error?.message}
                            isSearchable
                            useTooltipErrorMessage={false}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={1} display="flex" justifyContent="center">
                      <IconButton onClick={() => remove(index)}>
                        <CloseIcon />
                      </IconButton>
                    </Grid>
                  </Grid>

                  <TariffAccordionItem tariff={selectedTariff} wallboxType={wallbox.wallboxType} />
                </Stack>
              )
            })}
          </Stack>
        )}
      </Dialog>
    </FormProvider>
  )
}
